import { lazy, Suspense, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import TicketsPage from '@features/tickets/pages/tickets';
import { Loader } from '@ui/index';
import { menuConfig } from '../consts/navigation-menu';
import ProfilePage from '../features/profile';
import { NotFound404 } from './pages/NotFound404';
import Objects from './pages/Objects';

const ObjectsPage = lazy(() => import('../features/objects'));
const CallsPage = lazy(() => import('../features/calls'));
const ConnectionsPage = lazy(() => import('../features/connections'));
const NewsPage = lazy(() => import('../features/news'));
const EnterprisesNewsPage = lazy(() => import('../features/enterprises-news'));
const EquipmentPage = lazy(() => import('../features/equipment'));
const PeoplePage = lazy(() => import('../features/people'));
const UsersPage = lazy(() => import('../features/users'));
const RolesPage = lazy(() => import('../features/roles'));
const TenantsPage = lazy(() => import('../features/tenants'));
const RentObjectsPage = lazy(() => import('../features/rent-objects'));
const EnterprisesPage = lazy(() => import('../features/enterprises'));
const ParkingPage = lazy(() => import('../features/parking-slots'));
const ParkingLotsPage = lazy(() => import('../features/parking-lots'));
const CamerasPage = lazy(() => import('../features/cameras'));
const GuestsPage = lazy(() => import('../features/guests'));
const MarketCompanies = lazy(() => import('../features/market-companies'));
const MarketOffersPage = lazy(() => import('../features/market-offers'));
const VotingPage = lazy(() => import('../features/voting'));
const QuarantinePage = lazy(() => import('../features/quarantine'));
// const StatisticPage = lazy(() => import('../features/statistics'));
const TicketsDisplayPage = lazy(() => import('../features/tickets-display'));
const ReportsPage = lazy(() => import('@features/reports'));
const ScheduleSettingsPage = lazy(() => import('../features/schedule'));
const AlarmsPage = lazy(() => import('../features/alarms'));
const ChatsPage = lazy(() => import('../features/socket-chats-reworked'));
const EnterprisesCompanyPage = lazy(() => import('../features/enterprise-company'));
const EnterprisesEmployeePage = lazy(() => import('../features/enterprises-employee'));
const EnterprisesPassPage = lazy(() => import('../features/enterprises-pass'));

const EnterprisesContactsPage = lazy(() => import('../features/enterprises-contacts'));

const EnterprisesObjectsPage = lazy(() => import('../features/enterprises-objects'));

const ApartmentAcceptance = lazy(
  () => import('../features/apartment-acceptance/pages/schedule-page')
);
const AcceptanceDashboard = lazy(
  () => import('../features/apartment-acceptance/pages/dashboard-page')
);
const EnterprisesEventsLogPage = lazy(() => import('../features/events-log'));
const EnterprisesParkingSlotsPage = lazy(
  () => import('../features/enterprises-parking-slots')
);
const StuffPage = lazy(() => import('../features/stuff'));

const BarriersPage = lazy(() => import('../features/barriers'));
const JournalPage = lazy(() => import('../features/journal-user-actions'));
const DashboardPage = lazy(() => import('../features/dashboard'));
const DashboardClimatePage = lazy(() => import('@features/dashboard-climate'));
const ContainersPage = lazy(() => import('../features/containers'));
const AccessKeysPage = lazy(() => import('../features/access-keys'));
const EnterpriseContractingPage = lazy(() => import('../features/enterprise-contractor'));
const SecurityPage = lazy(() => import('../features/security'));
const UserAppPage = lazy(() => import('../features/user-app'));
const DoorsPage = lazy(() => import('../features/doors'));
const StatisticCommunicationPage = lazy(
  () => import('../features/statistics-communication')
);
const TboSchedulePage = lazy(() => import('../features/tbo-schedule'));
const JobSchedulePage = lazy(() => import('../features/job-schedule'));
const EventServicePage = lazy(() => import('../features/event-service'));
const DevicesHintsPage = lazy(() => import('../features/devices-hints'));

const EnterprisesTicketsPage = lazy(
  () => import('@features/enterprises-tickets/pages/tickets')
);
const EnterprisesTicketPage = lazy(
  () => import('@features/enterprises-tickets/pages/ticket')
);
const ActivityPage = lazy(() => import('@features/activity'));

const ComparisonsPage = lazy(
  () => import('../features/counters-check/pages/comparisons')
);
const ComparisonPage = lazy(
  () => import('../features/counters-check/pages/comparison-result')
);
const PlannedWorksPage = lazy(() => import('@features/planned-works'));
const PassConfigPage = lazy(() => import('@features/pass-config'));
const PlannedWorkSchedulePage = lazy(() => import('@features/planned-work-schedule'));

const DebtorsGeneralSettings = lazy(() => import('../features/debtors-settings'));
const DebtorsAccess = lazy(() => import('../features/debtors-access'));
const ConciergePage = lazy(() => import('@features/concierge'));
const EnterpriseVehiclesPage = lazy(() => import('@features/vehicles'));

/** Pages */
const RentPage = lazy(() => import('@pages/rent'));
const AccountsPage = lazy(() => import('@pages/accounts'));
const DebtorsPage = lazy(() => import('@pages/debtors-list'));
const AccessLevelPage = lazy(() => import('@pages/access-level'));
const UsersGroupsPage = lazy(() => import('@pages/users-groups'));
const SchedulePage = lazy(() => import('@pages/access-schedule'));
const TicketsTypesPage = lazy(() => import('@pages/tickets-types'));
const AccessGroupsPage = lazy(() => import('@pages/access-groups'));
const DebtorsReceipts = lazy(() => import('@pages/debtors-receipts'));
const DebtorsNotifications = lazy(() => import('@pages/debtors-notifications'));
const TypesGuests = lazy(() => import('@pages/types-guests'));
const CheckLists = lazy(() => import('@pages/check-lists'));
const CountersPage = lazy(() => import('@pages/counters'));
const CompanyPage = lazy(() => import('@pages/company'));
const InspectionsPage = lazy(() => import('@pages/inspections'));
const ClientPremisesPage = lazy(() => import('@pages/client-premises'));
const IndicatorsPanelPage = lazy(() => import('@pages/indicators-panel'));
const SmsGatewaySettingsPage = lazy(() => import('@pages/sms-gateway-settings'));
const EnterprisesNewPage = lazy(() => import('@pages/enterprises'));
const WorksPage = lazy(() => import('@pages/works'));
const WorkFlowsPage = lazy(() => import('@pages/work-flows'));
const Stories = lazy(() => import('@pages/stories'));
const AccountsNew = lazy(() => import('@pages/accounts-new'));
const QualificationsPage = lazy(() => import('@pages/qualifications'));
const EmployeeAssignment = lazy(() => import('@pages/employee-assignment'));
const ResidentsNewPage = lazy(() => import('@pages/residents-new'));
const PassesPage = lazy(() => import('@pages/passes'));
const PollsPage = lazy(() => import('@pages/polls'));
const BarriersNewPage = lazy(() => import('@pages/barriers-new'));
const TicketsFields = lazy(() => import('@pages/tickets-fields'));
const TicketsSchemas = lazy(() => import('@pages/tickets-schemas/pages/tickets-schemas'));
const TicketsSchema = lazy(() => import('@pages/tickets-schemas/pages/tickets-schema'));
const IntercomsPage = lazy(() => import('@pages/intercoms'));
const VehiclesPage = lazy(() => import('@pages/vehicles'));
const EventsLogPage = lazy(() => import('@pages/events-log'));
const AnalyticsPage = lazy(() => import('@pages/analytics'));
const DocumentFlowPage = lazy(() => import('@pages/document-flow'));
const AppealsPage = lazy(() => import('@pages/appeals'));

const useStyles = makeStyles({
  main: {
    display: 'grid',
    position: 'relative',
  },
});

export function Main() {
  const [title, setTitle] = useState('');
  const classes = useStyles();

  const location = useLocation();
  const { items } = menuConfig;

  const menu = items.reduce(
    (acc, { items, ...curr }) => acc.concat(items ? items : []).concat(curr),
    []
  );

  useEffect(() => {
    const pathname = location.pathname.split('/')[1];
    const { text } = menu.find((i) => pathname === i.link) || {};
    setTitle(text);
  }, [location]);

  return (
    <main className={classes.main}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{title}</title>
      </Helmet>
      <Suspense fallback={<Loader isLoading />}>
        <Switch>
          <Route exact path="/people" component={PeoplePage} />
          <Route exact path="/people/:id" component={PeoplePage} />
          <Route exact path="/users" component={UsersPage} />
          <Route path="/users/:id" component={UsersPage} />
          <Route path="/users" exact component={UsersPage} />
          <Route exact path="/roles" component={RolesPage} />
          <Route exact path="/objects-new" component={ObjectsPage} />
          <Route exact path="/objects-new/:other" component={ObjectsPage} />
          <Route exact path="/objects" component={Objects} />
          <Route exact path="/tenants" component={TenantsPage} />
          <Route exact path="/rent-objects" component={RentObjectsPage} />
          <Route exact path="/enterprises" component={EnterprisesPage} />
          <Route exact path="/parking" component={ParkingPage} />
          <Route exact path="/parking-lots" component={ParkingLotsPage} />
          <Route exact path="/containers" component={ContainersPage} />
          <Route exact path="/intercoms" component={IntercomsPage} />
          <Route exact path="/intercoms/:id" component={IntercomsPage} />
          <Route exact path="/cctv" component={CamerasPage} />
          <Route exact path="/cctv/:id" component={CamerasPage} />
          <Route exact path="/doors" component={DoorsPage} />
          <Route exact path="/barriers" component={BarriersPage} />
          <Route exact path="/guests" component={GuestsPage} />
          <Route exact path="/guests/:id" component={GuestsPage} />
          <Route exact path="/vehicles" component={VehiclesPage} />
          <Route exact path="/vehicles/:id" component={VehiclesPage} />
          <Route exact path="/market-companies" component={MarketCompanies} />
          <Route exact path="/market-companies/:id" component={MarketCompanies} />
          <Route exact path="/market-offers" component={MarketOffersPage} />
          <Route exact path="/market-offers/:id" component={MarketOffersPage} />
          <Route exact path="/company" component={CompanyPage} />
          <Route exact path="/news" component={NewsPage} />
          <Route exact path="/enterprises-news" component={EnterprisesNewsPage} />
          <Route exact path="/enterprises-news/:id" component={EnterprisesNewsPage} />
          <Route exact path="/voting" component={VotingPage} />
          <Route exact path="/voting/:id" component={VotingPage} />
          <Route exact path="/quarantine" component={QuarantinePage} />
          <Route exact path="/analytics" component={AnalyticsPage} />
          <Route exact path="/document-flow" component={DocumentFlowPage} />
          {/* <Route exact path="/statistics" component={StatisticPage} /> */}
          <Route exact path="/statistics" component={IndicatorsPanelPage} />
          <Route exact path="/counters" component={CountersPage} />
          <Route exact path="/tickets-display" component={TicketsDisplayPage} />
          <Route exact path="/tickets" component={TicketsPage} />
          <Route exact path="/tickets/:id" component={TicketsPage} />
          <Route path="/reports" component={ReportsPage} />
          <Route exact path="/alarms" component={AlarmsPage} />
          <Route exact path="/appointment" component={ScheduleSettingsPage} />
          <Route exact path="/calls" component={CallsPage} />
          <Route exact path="/connections" component={ConnectionsPage} />
          <Route exact path="/passes" component={PassesPage} />
          <Route exact path="/passes/:id" component={PassesPage} />
          <Route exact path="/profile" component={ProfilePage} />
          <Route exact path="/enterprise-profile" component={ProfilePage} />
          <Route exact path="/events-log" component={EventsLogPage} />
          <Route exact path="/equipment" component={EquipmentPage} />
          <Route exact path="/access-keys" component={AccessKeysPage} />
          <Route exact path="/access-groups" component={AccessGroupsPage} />
          <Route exact path="/access-groups/:id" component={AccessGroupsPage} />
          <Route exact path="/enterprises-vehicles" component={EnterpriseVehiclesPage} />
          <Route exact path="/activity" component={ActivityPage} />
          <Route path="/enterprises-tickets/:id" component={EnterprisesTicketPage} />
          <Route exact path="/enterprises-tickets" component={EnterprisesTicketsPage} />
          <Route exact path="/enterprises-events" component={EnterprisesEventsLogPage} />
          <Route
            exact
            path="/statistics-communication"
            component={StatisticCommunicationPage}
          />
          {/* <Route exact path="/indicators-panel" component={IndicatorsPanelPage} /> */}
          <Route exact path="/users-groups" component={UsersGroupsPage} />
          <Route exact path="/users-groups/:id" component={UsersGroupsPage} />
          <Route exact path="/access-level" component={AccessLevelPage} />
          <Route exact path="/access-level/:id" component={AccessLevelPage} />
          <Route exact path="/schedule" component={SchedulePage} />
          <Route exact path="/schedule/:id" component={SchedulePage} />
          <Route
            exact
            path="/enterprise-contractor"
            component={EnterpriseContractingPage}
          />
          <Route
            exact
            path="/enterprise-contractor/:id"
            component={EnterpriseContractingPage}
          />
          <Route exact path="/enterprises-company" component={EnterprisesCompanyPage} />
          <Route exact path="/enterprises-employee" component={EnterprisesEmployeePage} />
          <Route exact path="/enterprises-passes" component={EnterprisesPassPage} />
          <Route exect path="/enterprises-contacts" component={EnterprisesContactsPage} />
          <Route exect path="/enterprises-objects" component={EnterprisesObjectsPage} />
          <Route path="/apartment-acceptance/:id" component={ApartmentAcceptance} />
          <Route exact path="/apartment-acceptance" component={AcceptanceDashboard} />
          <Route
            exact
            path="/enterprises-parking"
            component={EnterprisesParkingSlotsPage}
          />
          <Route path="/enterprises-stuff" component={StuffPage} />
          <Route exact path="/journal-user-actions" component={JournalPage} />
          <Route path="/messages" component={ChatsPage} />
          <Route path="/polls" component={PollsPage} />
          <Route path="/stuff" component={StuffPage} />
          <Route path="/security" component={SecurityPage} />
          <Route path="/user-app" component={UserAppPage} />
          <Route path="/client-premises" component={ClientPremisesPage} />
          <Route path="/sms-gateway-settings" component={SmsGatewaySettingsPage} />
          <Route path="/tbo-schedule" component={TboSchedulePage} />
          <Route path="/job-schedule" component={JobSchedulePage} />
          <Route exact path="/" render={() => <Redirect to="/profile" />} />
          <Route path="/works" component={WorksPage} />
          <Route path="/works/:id" component={WorksPage} />
          <Route exact path="/dashboard" component={DashboardPage} />
          <Route exact path="/dashboard-climate" component={DashboardClimatePage} />
          <Route exact path="/event-service" component={EventServicePage} />
          <Route exact path="/tickets-types" component={TicketsTypesPage} />
          <Route exact path="/tickets-types/:id" component={TicketsTypesPage} />
          <Route exact path="/devices-hints" component={DevicesHintsPage} />
          <Route exact path="/counters-check" component={ComparisonsPage} />
          <Route path="/counters-check/:id" component={ComparisonPage} />
          <Route exact path="/planned-works" component={PlannedWorksPage} />
          <Route exact path="/planned-works/:id" component={PlannedWorksPage} />
          <Route exact path="/pass-config" component={PassConfigPage} />
          <Route exact path="/qualifications" component={QualificationsPage} />
          <Route exact path="/qualifications/:id" component={QualificationsPage} />
          <Route
            exact
            path="/planned-work-schedule"
            component={PlannedWorkSchedulePage}
          />
          <Route exact path="/work-flows" component={WorkFlowsPage} />
          <Route exact path="/work-flows/:id" component={WorkFlowsPage} />
          <Route exact path="/debtors-list" component={DebtorsPage} />
          <Route exact path="/debtors-list/:id" component={DebtorsPage} />
          <Route exact path="/debtors-notifications" component={DebtorsNotifications} />
          <Route exact path="/debtors-receipts" component={DebtorsReceipts} />
          <Route exact path="/debtors-settings" component={DebtorsGeneralSettings} />
          <Route exact path="/debtors-access" component={DebtorsAccess} />
          <Route exact path="/accounts" component={AccountsPage} />
          <Route exact path="/accounts/:id" component={AccountsPage} />
          <Route exact path="/concierge" component={ConciergePage} />
          <Route exact path="/check-lists" component={CheckLists} />
          <Route exact path="/check-lists/:id" component={CheckLists} />
          <Route exact path="/inspections" component={InspectionsPage} />
          <Route exact path="/inspections/:id" component={InspectionsPage} />
          <Route exact path="/rent" component={RentPage} />
          <Route exact path="/rent/:id" component={RentPage} />
          <Route exact path="/guests-types" component={TypesGuests} />
          <Route exact path="/guests-types/:id" component={TypesGuests} />
          <Route exact path="/enterprise-list" component={EnterprisesNewPage} />
          <Route exact path="/enterprise-list/:id" component={EnterprisesNewPage} />
          <Route exact path="/stories" component={Stories} />
          <Route exact path="/stories/:id" component={Stories} />
          <Route exact path="/accounts-new" component={AccountsNew} />
          <Route exact path="/accounts-new/:id" component={AccountsNew} />
          <Route exact path="/employee-assignment" component={EmployeeAssignment} />
          <Route exact path="/residents-new" component={ResidentsNewPage} />
          <Route exact path="/residents-new/:id" component={ResidentsNewPage} />
          <Route exact path="/residents-new/:id/appeals" component={ResidentsNewPage} />
          <Route exact path="/barriers-new" component={BarriersNewPage} />

          <Route exact path="/tickets-fields" component={TicketsFields} />
          <Route exact path="/tickets-fields/:id" component={TicketsFields} />
          <Route exact path="/tickets-schemas" component={TicketsSchemas} />
          <Route exact path="/tickets-schemas/:id" component={TicketsSchema} />
          <Route exact path="/tickets-schemas/creation" component={TicketsSchema} />

          <Route exact path="/appeals" component={AppealsPage} />
          <Route exact path="/appeals/:id" component={AppealsPage} />

          <Route component={NotFound404} />
        </Switch>
      </Suspense>
    </main>
  );
}
